import React, { forwardRef } from "react";

import { cn } from "../../utils/cn";

// Button variants
export type ButtonVariant =
	| "primary"      // Blue background with white text (like "Crea account")
	| "secondary"    // Outlined with blue border and blue text (like "Precedente")
	| "outline"      // Transparent with border
	| "ghost"        // No background, no border
	| "danger"       // Red variant for destructive actions
	| "success"      // Green variant for success actions
	| "warning";     // Yellow variant for warning actions

// Button sizes
export type ButtonSize = "sm" | "md" | "lg" | "xl";

// Button states
export type ButtonState = "default" | "loading" | "disabled" | "success";

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "size"> {
	// Content
	children: React.ReactNode;

	// Variants and styling
	variant?: ButtonVariant;
	buttonSize?: ButtonSize; // Renamed to avoid HTML attribute conflict
	state?: ButtonState;

	// Layout and spacing
	fullWidth?: boolean;
	rounded?: boolean; // For pill-shaped buttons like Figma design

	// Icons
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;
	iconOnly?: boolean;

	// Customization
	className?: string;

	// Loading state
	loadingText?: string;

	// Events
	onClick?: () => void;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
	// Content
	children,

	// Variants and styling
	variant = "primary",
	buttonSize = "md",
	state = "default",

	// Layout and spacing
	fullWidth = false,
	rounded = true, // Default to rounded like Figma design

	// Icons
	leftIcon,
	rightIcon,
	iconOnly = false,

	// Customization
	className,

	// Loading state
	loadingText,

	// Events
	onClick,

	// HTML button props
	disabled,
	type = "button",
	...props
}, ref) => {
	// Determine if button should be disabled
	const isDisabled = disabled || state === "disabled" || state === "loading";

	// Size mappings
	const getSizeClasses = () => {
		switch (buttonSize) {
			case "sm":
				return {
					padding: "px-4 py-2",
					textSize: "txt-14",
					iconSize: "w-4 h-4",
					minHeight: "min-h-8"
				};
			case "md":
				return {
					padding: "px-6 py-3",
					textSize: "txt-16",
					iconSize: "w-5 h-5",
					minHeight: "min-h-10"
				};
			case "lg":
				return {
					padding: "px-8 py-4", // Matches Figma design exactly
					textSize: "txt-18",
					iconSize: "w-6 h-6",
					minHeight: "min-h-12"
				};
			case "xl":
				return {
					padding: "px-10 py-5",
					textSize: "txt-25",
					iconSize: "w-7 h-7",
					minHeight: "min-h-14"
				};
			default:
				return {
					padding: "px-6 py-3",
					textSize: "txt-16",
					iconSize: "w-5 h-5",
					minHeight: "min-h-10"
				};
		}
	};

	// Variant-based styling
	const getVariantClasses = () => {
		switch (variant) {
			case "primary":
				return {
					bg: "bg-blu-medio",
					text: "text-bianco",
					border: "border-blu-medio",
					hover: "hover:bg-blu-chiaro hover:border-blu-chiaro",
					focus: "focus:ring-2 focus:ring-blu-medio/20",
					disabled: "bg-grigio border-grigio text-grigio"
				};
			case "secondary":
				return {
					bg: "bg-transparent",
					text: "text-blu-medio",
					border: "border-blu-medio",
					hover: "hover:bg-blu-medio hover:text-bianco",
					focus: "focus:ring-2 focus:ring-blu-medio/20",
					disabled: "border-grigio text-grigio"
				};
			case "outline":
				return {
					bg: "bg-transparent",
					text: "text-blu-medio",
					border: "border-blu-medio border-1",
					hover: "hover:bg-blu-medio hover:text-bianco",
					focus: "focus:ring-2 focus:ring-blu-scuro/20",
					disabled: "border-grigio text-grigio"
				};
			case "ghost":
				return {
					bg: "bg-transparent",
					text: "text-blu-scuro",
					border: "border-transparent",
					hover: "hover:bg-grigio/20",
					focus: "focus:ring-2 focus:ring-blu-scuro/20",
					disabled: "text-grigio"
				};
			case "danger":
				return {
					bg: "bg-rosso",
					text: "text-bianco",
					border: "border-rosso",
					hover: "hover:bg-rosso/90 hover:border-rosso/90",
					focus: "focus:ring-2 focus:ring-rosso/20",
					disabled: "bg-grigio border-grigio text-grigio"
				};
			case "success":
				return {
					bg: "bg-verde",
					text: "text-blu-scuro",
					border: "border-verde",
					hover: "hover:bg-verde/90 hover:border-verde/90",
					focus: "focus:ring-2 focus:ring-verde/20",
					disabled: "bg-grigio border-grigio text-grigio"
				};
			case "warning":
				return {
					bg: "bg-giallo",
					text: "text-blu-scuro",
					border: "border-giallo",
					hover: "hover:bg-giallo/90 hover:border-giallo/90",
					focus: "focus:ring-2 focus:ring-giallo/20",
					disabled: "bg-grigio border-grigio text-grigio"
				};
			default:
				return {
					bg: "bg-blu-medio",
					text: "text-bianco",
					border: "border-blu-medio",
					hover: "hover:bg-blu-chiaro hover:border-blu-chiaro",
					focus: "focus:ring-2 focus:ring-blu-medio/20",
					disabled: "bg-grigio border-grigio text-grigio"
				};
		}
	};

	// State-based styling
	const getStateClasses = () => {
		if (state === "loading") {
			return "cursor-wait";
		}

		if (state === "success") {
			return "cursor-default";
		}

		if (isDisabled) {
			return "cursor-not-allowed opacity-60";
		}

		return "cursor-pointer";
	};

	const sizes = getSizeClasses();
	const variants = getVariantClasses();
	const stateClasses = getStateClasses();

	// Loading spinner component
	const LoadingSpinner = () => (
		<svg
			className={cn("animate-spin text-blu-medio", sizes.iconSize)}
			fill="none"
			viewBox="0 0 24 24"
		>
			<circle
				className="opacity-25"
				cx="12"
				cy="12"
				r="10"
				stroke="currentColor"
				strokeWidth="4"
			/>
			<path
				className="opacity-75"
				fill="currentColor"
				d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
			/>
		</svg>
	);

	// Success checkmark component
	const SuccessCheckmark = () => (
		<svg
			className={cn("text-verde", sizes.iconSize)}
			fill="currentColor"
			viewBox="0 0 20 20"
		>
			<path
				fillRule="evenodd"
				d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
				clipRule="evenodd"
			/>
		</svg>
	);

	return (
		<button
			ref={ref}
			type={type}
			disabled={isDisabled}
			className={cn(
				// Base classes
				"inline-flex items-center justify-center gap-2 font-medium transition-all duration-200",
				"focus:outline-none active:scale-95",

				// Size and padding
				sizes.padding,
				sizes.minHeight,

				// Border radius
				rounded ? "rounded-[100px]" : "rounded-lg", // Figma design uses 100px radius

				// Variant-based styling
				isDisabled ? variants.disabled : variants.bg,
				isDisabled ? variants.disabled : variants.text,
				isDisabled ? variants.disabled : variants.border,

				// Hover and focus states (only when not disabled)
				!isDisabled && variants.hover,
				!isDisabled && variants.focus,

				// State-based styling
				stateClasses,

				// Layout
				fullWidth && "w-full",
				iconOnly && "aspect-square",

				// Custom classes
				className
			)}
			onClick={onClick}
			{...props}
		>
			{/* Left icon or loading spinner */}
			{state === "loading" ? (
				<LoadingSpinner />
			) : state === "success" ? (
				<SuccessCheckmark />
			) : (
				leftIcon && <span className={sizes.iconSize}>{leftIcon}</span>
			)}

			{/* Button content */}
			{!iconOnly && (
				<span
					className={cn(
						// Use Tailwind custom classes directly
						sizes.textSize,
						"font-medium leading-[1.2]", // Matches Figma design
						state === "loading" && loadingText ? "ml-2" : "",
						// Ensure proper text color for different states
						state === "loading" && "text-blu-medio",
						state === "success" && "text-bianco",
						isDisabled && state !== "loading" && "text-grigio"
					)}
				>
					{state === "loading" && loadingText ? loadingText : children}
				</span>
			)}

			{/* Right icon */}
			{!iconOnly && rightIcon && state !== "loading" && state !== "success" && (
				<span className={sizes.iconSize}>{rightIcon}</span>
			)}
		</button>
	);
});

Button.displayName = "Button";

// Convenience components for common button types
export const PrimaryButton: React.FC<Omit<ButtonProps, "variant"> & { variant?: never }> = (props) => (
	<Button variant="primary" {...props} />
);

export const SecondaryButton: React.FC<Omit<ButtonProps, "variant"> & { variant?: never }> = (props) => (
	<Button variant="secondary" {...props} />
);

export const OutlineButton: React.FC<Omit<ButtonProps, "variant"> & { variant?: never }> = (props) => (
	<Button variant="outline" {...props} />
);

export const GhostButton: React.FC<Omit<ButtonProps, "variant"> & { variant?: never }> = (props) => (
	<Button variant="ghost" {...props} />
);

export const DangerButton: React.FC<Omit<ButtonProps, "variant"> & { variant?: never }> = (props) => (
	<Button variant="danger" {...props} />
);

export const SuccessButton: React.FC<Omit<ButtonProps, "variant"> & { variant?: never }> = (props) => (
	<Button variant="success" {...props} />
);

export const WarningButton: React.FC<Omit<ButtonProps, "variant"> & { variant?: never }> = (props) => (
	<Button variant="warning" {...props} />
);

// Icon button component
export const IconButton: React.FC<Omit<ButtonProps, "iconOnly"> & { iconOnly?: never }> = (props) => (
	<Button iconOnly {...props} />
);
